import { OpponentResponseDto } from "@/lib/api/interfaces/opponents.interface";
import { useFormConflictError } from "@/lib/hooks/use-form-conflict-error";
import {
  useOpponentsApplyLogoEdit,
  useOpponentsCreate,
  useOpponentsPreviewBackgroundRemoval,
  useOpponentsUploadLogo,
} from "@/lib/api/requests/opponents.requests";
import { toast } from "@/lib/toast";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/ui-components/ui/form";
import { Input } from "@/ui-components/ui/input";
import { zodResolver } from "@hookform/resolvers/zod";
import { Flag } from "lucide-react";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import z from "zod";
import { CreateBaseDialog } from "@/util-components/dialog/base-dialogs";
import { ManagedImageField, persistManagedImageDraft } from "@/util-components/media";

const createOpponentSchema = z.object({
  name: z.string().min(1, { message: "Name ist erforderlich" }),
  logo: z.instanceof(File).optional(),
});

type CreateOpponentFormValues = z.infer<typeof createOpponentSchema>;

const CreateOpponentDialog = ({
  open,
  onClose,
  onCreated,
}: {
  open: boolean;
  onClose: (refetchData: boolean) => void;
  onCreated?: (opponent: OpponentResponseDto) => void;
}) => {
  const [isSaving, setIsSaving] = useState(false);
  const [canSave, setCanSave] = useState(false);

  return (
    <CreateBaseDialog
      open={open}
      onClose={onClose}
      icon={Flag}
      title="Gegner hinzufügen"
      description="Erstelle einen neuen Gegner für Spiele."
      isSaving={isSaving}
      canSave={canSave}
      formId="create-opponent-form"
      maxWidth="lg"
    >
      <CreateOpponentForm
        onFormSubmit={(opponent) => {
          onClose(true);
          if (opponent) {
            onCreated?.(opponent);
          }
        }}
        setIsSaving={setIsSaving}
        setCanSave={setCanSave}
      />
    </CreateBaseDialog>
  );
};

const CreateOpponentForm = ({
  onFormSubmit,
  setIsSaving,
  setCanSave,
}: {
  onFormSubmit: (opponent?: OpponentResponseDto) => void;
  setIsSaving: (val: boolean) => void;
  setCanSave: (val: boolean) => void;
}) => {
  const { mutateAsync: createOpponent, isPending: isCreating } = useOpponentsCreate(undefined, {
    skipGlobalErrorHandler: true,
    throwOnError: true,
  });
  const { mutateAsync: uploadLogo, isPending: isUploading } = useOpponentsUploadLogo();
  const { mutateAsync: applyLogoEdit, isPending: isApplyingLogoEdit } = useOpponentsApplyLogoEdit();
  const { mutateAsync: previewBackgroundRemoval } = useOpponentsPreviewBackgroundRemoval();

  const form = useForm<CreateOpponentFormValues>({
    resolver: zodResolver(createOpponentSchema),
    mode: "onChange",
    defaultValues: {
      name: "",
      logo: undefined,
    },
  });

  const { handleConflictError } = useFormConflictError(form);
  const logoFile = form.watch("logo");
  const isPending = isCreating || isUploading || isApplyingLogoEdit;

  useEffect(() => {
    setIsSaving(isPending);
  }, [isPending, setIsSaving]);

  useEffect(() => {
    setCanSave(form.formState.isValid);
  }, [form.formState.isValid, setCanSave]);

  const onSubmit = async (data: CreateOpponentFormValues) => {
    try {
      const createdOpponent = await createOpponent({ name: data.name });

      await persistManagedImageDraft({
        entityId: createdOpponent._id,
        file: data.logo,
        uploadImage: uploadLogo,
        applyImageEdit: applyLogoEdit,
      });

      toast.success(data.logo ? "Gegner und Wappen erfolgreich erstellt" : "Gegner wurde erfolgreich erstellt");
      onFormSubmit(createdOpponent);
    } catch (error) {
      handleConflictError(error as Error, "Gegner konnte nicht erstellt werden");
    }
  };

  return (
    <Form {...form}>
      <form id="create-opponent-form" onSubmit={form.handleSubmit(onSubmit)} className="flex min-h-0 flex-1 flex-col overflow-hidden">
        <div className="flex-1 space-y-6 overflow-y-auto p-6">
          <FormField
            control={form.control}
            name="logo"
            render={() => (
              <FormItem>
                <FormControl>
                  <ManagedImageField
                    file={logoFile}
                    onFileChange={(nextFile) => form.setValue("logo", nextFile ?? undefined, { shouldDirty: true, shouldValidate: true })}
                    disabled={isPending}
                    emptyTitle="Wappen auswählen"
                    previewBackgroundRemoval={async (file) => previewBackgroundRemoval({ file })}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="name"
            render={({ field }) => (
              <FormItem className="space-y-3">
                <FormLabel className="text-sm font-medium text-gray-700">
                  Vereinsname <span className="text-red-500">*</span>
                </FormLabel>
                <FormControl>
                  <Input
                    placeholder="z.B. FC Bayern"
                    {...field}
                    className="h-11 border-gray-200 transition-all focus:border-blue-500 focus:ring-blue-500/20"
                    disabled={isPending}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>
      </form>
    </Form>
  );
};

export default CreateOpponentDialog;
